import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { listQuotes } from '../lib/admin.js';
import { money, quoteState, daysAgo } from '../lib/quote.js';
import { idOf, useApp } from '../lib/app-state.js';
import { toast } from '../lib/server.js';

export default function QuotesPage() {
  const app = useApp();
  const { route } = useLocation();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [rows, setRows] = useState(null);
  const [page, setPage] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const timer = useRef(0);

  const load = (after = null) =>
    listQuotes({ search, filter, after })
      .then((r) => {
        setRows((prev) => (after ? [...(prev || []), ...r.quotes] : r.quotes));
        setPage(r.pageInfo);
      })
      .catch((e) => toast(e.message, true));

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => load(), search ? 300 : 0);
    return () => clearTimeout(timer.current);
  }, [search, filter]);

  const open = (q) => route(`/quotes/${idOf(q.id)}`);
  const gmail = app.config?.gmail;

  return (
    <s-page heading="Quotes" inlineSize="large">
      <s-button slot="primary-action" variant="primary" href="/quotes/new">
        New quote
      </s-button>
      <s-button slot="secondary-actions" href="/email">
        Email a customer
      </s-button>

      {!gmail?.connected && (
        <s-banner tone="warning" heading="Gmail is not connected yet">
          <s-paragraph>
            Quotes can still go out through Shopify's invoice email. Connect Gmail to send from your own mailbox and see
            replies here.
          </s-paragraph>
          <s-button slot="secondary-actions" href="/settings">
            Settings
          </s-button>
        </s-banner>
      )}

      <s-section padding="none" accessibilityLabel="Quotes">
        <s-box padding="base">
          <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
            <s-search-field
              label="Search quotes"
              labelAccessibilityVisibility="exclusive"
              placeholder="Search by customer, email or quote number"
              value={search}
              onInput={(e) => setSearch(e.currentTarget.value)}
            />
            <s-select label="Show" labelAccessibilityVisibility="exclusive" value={filter} onChange={(e) => setFilter(e.currentTarget.value)}>
              <s-option value="all">All quotes</s-option>
              <s-option value="open">Not sent</s-option>
              <s-option value="sent">Sent, not paid</s-option>
              <s-option value="paid">Paid</s-option>
            </s-select>
          </s-grid>
        </s-box>

        {rows === null && (
          <s-box padding="large">
            <s-stack alignItems="center">
              <s-spinner accessibilityLabel="Loading quotes" />
            </s-stack>
          </s-box>
        )}

        {rows && rows.length === 0 && (
          <s-box padding="large-200">
            <s-stack alignItems="center" gap="base">
              <s-heading>{search || filter !== 'all' ? 'No quotes match' : 'No quotes yet'}</s-heading>
              <s-paragraph color="subdued">Build one in under a minute: pick the customer, add paint or products, send.</s-paragraph>
              <s-button variant="primary" href="/quotes/new">
                New quote
              </s-button>
            </s-stack>
          </s-box>
        )}

        {rows && rows.length > 0 && (
          <s-table>
            <s-table-header-row>
              <s-table-header listSlot="primary">Quote</s-table-header>
              <s-table-header listSlot="secondary">Customer</s-table-header>
              <s-table-header>Items</s-table-header>
              <s-table-header listSlot="labeled" format="currency">
                Total
              </s-table-header>
              <s-table-header listSlot="inline">Status</s-table-header>
              <s-table-header>Updated</s-table-header>
            </s-table-header-row>
            <s-table-body>
              {rows.map((q) => {
                const st = quoteState(q);
                return (
                  <s-table-row key={q.id} clickDelegate={`q-${idOf(q.id)}`}>
                    <s-table-cell>
                      <s-link id={`q-${idOf(q.id)}`} href={`/quotes/${idOf(q.id)}`} onClick={(e) => { e.preventDefault(); open(q); }}>
                        {q.name}
                      </s-link>
                      {q.option && <s-text color="subdued"> · {q.option}</s-text>}
                    </s-table-cell>
                    <s-table-cell>{q.customer?.displayName || q.email || '—'}</s-table-cell>
                    <s-table-cell>
                      <s-text color="subdued">{q.preview.slice(0, 2).join(', ')}{q.items > 2 ? ` +${q.items - 2}` : ''}</s-text>
                    </s-table-cell>
                    <s-table-cell>{money(q.total)}</s-table-cell>
                    <s-table-cell>
                      <s-badge tone={st.tone}>{st.label}</s-badge>
                    </s-table-cell>
                    <s-table-cell>{daysAgo(q.updatedAt)}</s-table-cell>
                  </s-table-row>
                );
              })}
            </s-table-body>
          </s-table>
        )}

        {page?.hasNextPage && (
          <s-box padding="base">
            <s-stack alignItems="center">
              <s-button
                loading={loadingMore}
                onClick={async () => {
                  setLoadingMore(true);
                  await load(page.endCursor);
                  setLoadingMore(false);
                }}
              >
                Show more
              </s-button>
            </s-stack>
          </s-box>
        )}
      </s-section>
    </s-page>
  );
}
